import React, { useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import {
  TruncateAndTooltip,
  Badge,
  Icon,
  toastShow,
  TOAST_VARIANT_SUCCESS,
  DataListRow,
  DataListExtraColumn,
  Grid,
  GridCell,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  Flex,
  FlexItem,
  TEXT_VARIANT_SMALL_LIGHT,
  Text,
  TEXT_VARIANT_BOLD_LIGHT,
  TEXT_VARIANT_LIGHT,
  SpacingVertical,
  SPACING_16,
  SkeletonLoader,
  SKELETON_LOADER_VARIANT_ONWHITE
} from 'mailup-web-components'

import ResultCollaboration from './ResultCollaboration'
import ResultThumbnail from './ResultThumbnail'
import ResultCheckbox from './ResultCheckbox'

import { storeGet } from 'Helpers/localStorage'
import {
  STORE_KEY_LIST,
  MAX_TAGS_PER_ROW,
  ENTITY_TYPE_TRIGGER,
  ENTITY_TYPE_AUTOMATION_WORKFLOW
} from 'Constants'
import { fromUTCtoZoneFormatted } from 'Helpers/dates'
import { notifyParent } from 'Helpers/notifyParent'
import evalCondition from 'Helpers/evalCondition'

import {
  useRecoilCallback,
  useRecoilValueLoadable,
  useSetRecoilState,
  useRecoilValue
} from 'recoil'

import {
  searchMessagesCallback,
  copyMessageCallback,
  unarchiveMessagesCallback,
  setPublicMessagesCallback,
  isSendingRunningMessageCallback,
  getEntitiesThatUseMessageCallback,
  downloadHtmlMessageCallback,
  downloadPdfMessageCallback
} from '../../../recoil/callbacks/callbacks'

import {
  modalSendingState,
  modalMessageUsageState,
  modalDeleteState
} from 'Pages/MessageList/recoil/state/page'

import {
  isMessageUsed,
  messageSentCountState
} from 'Pages/MessageList/recoil/state/results'

import useMessageSendAction from 'Hooks/useMessageSendAction'

import './Result.scss'

import styled from '@emotion/styled'
import { Actions } from 'Components/Actions'

const ACTION_OVERVIEW = 'overview'
const ACTION_EDIT = 'edit'
const ACTION_SEND = 'send'
const ACTION_QUICKSEND = 'quicksend'
const ACTION_COPY = 'copy'
const ACTION_STATISTICS = 'statistics'
const ACTION_LAST_SENDING_STATS = 'last-sending-stats'

const ACTION_UNARCHIVE = 'unarchive'
const ACTION_UNPUBLISH = 'unpublish'
const ACTION_DELETE = 'delete'

const MESSAGE_TYPE_CONFIRMATION = 'Confirmation'
const MESSAGE_TYPE_WELCOME = 'Welcome'

const ACTION_DOWNLOAD_HTML = 'download_html'
const ACTION_DOWNLOAD_PDF = 'download_pdf'

const TypeSection = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;

  width: 100%;
  .margin-left {
    margin-left: 4px;
  }
`

const TypeComponent = ({ Id, Subject, isActiveMessage, isUsed, mode }) => {
  const intl = useIntl()
  return (
    <TypeSection>
      <Text variant={TEXT_VARIANT_BOLD_LIGHT} htmlElement={'span'}>
        {intl.formatMessage({
          id: 'Load.MailupCommon.Type'
        })}
        {':'}
      </Text>
      <Badge
        text={
          mode === MESSAGE_TYPE_WELCOME
            ? intl.formatMessage({ id: 'Load.Subscriptions.WelcomeEmail' })
            : intl.formatMessage({
                id: 'Load.Common.ConfirmationRequestEmailInitial'
              })
        }
        kind='outline'
        classIcon={undefined}
        type='default'
        className='margin-left'
      />
      {isActiveMessage ? (
        <Badge
          type='default'
          kind='outline'
          text={intl.formatMessage({
            id: 'Load.MailupCommon.Default'
          })}
        />
      ) : (
        isUsed && (
          <Badge
            type='default'
            kind='outline'
            text={intl.formatMessage({
              id: 'Load.MailupCommon.InUse'
            })}
          />
        )
      )}
    </TypeSection>
  )
}

export const Result = ({
  message,
  hideCollaboration,
  collaborationEnabled,
  buyCollaboration,
  loading
}) => {
  const intl = useIntl()
  const history = useHistory()

  const { TimeZoneCode: tzcode, EnableWebLibrary } = storeGet(STORE_KEY_LIST)
  const {
    Id,
    Subject,
    LastUpdated,
    LastSent,
    Note,
    Published,
    Archived,
    tags,
    isActiveMessage,
    IsBee,
    MessageType
  } = message

  const isUsed = useRecoilValue(isMessageUsed(Id))
  const { current: hasStats } = useRef(evalCondition('uo:STAT'))
  const { current: canUnpublish } = useRef(evalCondition('NOT uo:NONL'))
  const { current: canEditBee } = useRef(
    evalCondition(
      'NOT uo:NONL AND uo:BEEEDITORENABLE AND co:BEEEDITORENABLE'
    ) && !!IsBee
  )
  const { current: canEditHtml } = useRef(
    evalCondition('NOT uo:NONL') && !IsBee
  )
  const { current: canSend } = useRef(
    evalCondition('NOT uo:NOENQUEUE') && !Archived
  )
  const { current: canQuickSend } = useRef(evalCondition('NOT uo:NOSEND'))
  const { current: canCopy } = useRef(evalCondition('NOT uo:NONL'))

  const downloadHtml = useRecoilCallback(downloadHtmlMessageCallback, [])
  const downloadPdf = useRecoilCallback(downloadPdfMessageCallback, [])

  const fetchResults = useRecoilCallback(searchMessagesCallback, [])
  const copyMessage = useRecoilCallback(copyMessageCallback, [])
  const unarchiveMessages = useRecoilCallback(unarchiveMessagesCallback, [])
  const setPublicMessages = useRecoilCallback(setPublicMessagesCallback, [])
  const isSendingRunningMessage = useRecoilCallback(
    isSendingRunningMessageCallback,
    []
  )
  const getEntitiesThatUseMessage = useRecoilCallback(
    getEntitiesThatUseMessageCallback,
    []
  )

  const setModalSending = useSetRecoilState(modalSendingState)
  const setModalUsage = useSetRecoilState(modalMessageUsageState)
  const setModalDelete = useSetRecoilState(modalDeleteState)

  const handleSend = useMessageSendAction(Id)

  const sentCount = LastSent
    ? useRecoilValueLoadable(messageSentCountState(Id))
    : 0

  const { current: onCopyMessageSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({ id: 'Load.Alerts.SuccessCopy' }),
      TOAST_VARIANT_SUCCESS,
      'SuccessCopy',
      true
    )
    fetchResults()
  })

  const { current: onUnarchiveMessagesSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({ id: 'Load.MailupCommon.OperationSucces' }),
      TOAST_VARIANT_SUCCESS,
      'UnarchiveSuccess',
      true
    )
    fetchResults()
  })

  const { current: onUnpublishMessagesSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({ id: 'Load.EmailMessageList.UnpublishSuccess' }),
      TOAST_VARIANT_SUCCESS,
      'UnpublishSuccess',
      true
    )
    fetchResults()
  })

  const { current: onDeleteMessagesSuccess } = useRef(() => {
    toastShow(
      intl.formatMessage({
        id: 'Load.ActiveRecipientSummary.SuccessDeleteMessageSingluar'
      }),
      TOAST_VARIANT_SUCCESS,
      'SuccessDeleteMessageSingluar',
      true
    )
    fetchResults()
  })

  const editMessage = idMessage => {
    isSendingRunningMessage(Id).then(isSending => {
      if (isSending) {
        setModalSending(true)
      } else if (IsBee) {
        history.push(`/messages/email/bee/edit/${Id}`)
      } else {
        notifyParent('redirectMainFrame', [
          `form_upd_newsletter.aspx?isNew=true&idNL=${Id}`
        ])
      }
    })
  }

  const sendMessage = idMessage => {
    getEntitiesThatUseMessage(idMessage).then(entities => {
      const usage = entities.find(
        o =>
          o.Type === ENTITY_TYPE_TRIGGER ||
          o.Type === ENTITY_TYPE_AUTOMATION_WORKFLOW
      )
      if (usage) {
        setModalUsage({
          show: true,
          entityId: usage.Id,
          entityName: usage.Name,
          idMessage
        })
      } else {
        handleSend()
      }
    })
  }

  const handleHtmlDownload = useCallback(async () => {
    const fileContent = await downloadHtml(Id)
    if (fileContent) {
      const element = document.createElement('a')
      element.href = `data:text/html,${encodeURIComponent(fileContent)}`
      element.download = `${Id}.html`
      document.body.appendChild(element)
      element.click()
      element.parentNode.removeChild(element)
    }
  }, [])

  const handlePdfDownload = useCallback(async () => {
    const fileContent = await downloadPdf(Id)
    if (fileContent) {
      const element = document.createElement('a')
      element.href = `data:application/octet-stream;base64,${encodeURIComponent(
        fileContent
      )}`
      element.download = `${Id}.pdf`
      document.body.appendChild(element)
      element.click()
      element.parentNode.removeChild(element)
    }
  }, [])

  const handleClickAction = action => {
    switch (action) {
      case ACTION_OVERVIEW:
        history.push(`/messages/email/${Id}/overview?source=all&op=mod`)
        break
      case ACTION_EDIT:
        editMessage(Id)
        break
      case ACTION_SEND:
        sendMessage(Id)
        break
      case ACTION_QUICKSEND:
        notifyParent('quickSendMessage', [Id])
        break
      case ACTION_COPY:
        copyMessage(Id, onCopyMessageSuccess)
        break
      case ACTION_STATISTICS:
        notifyParent('redirectMainFrame', [
          `statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${Id}`
        ])
        break
      case ACTION_LAST_SENDING_STATS:
        notifyParent('redirectMainFrame', [
          `statistics_app_redirector.aspx?r=/stats/sendouts/list/mode/sendings/msg/${Id}`
        ])
        break
      case ACTION_UNARCHIVE:
        unarchiveMessages([Id], onUnarchiveMessagesSuccess)
        break
      case ACTION_UNPUBLISH:
        setPublicMessages([Id], false, onUnpublishMessagesSuccess)
        break
      case ACTION_DELETE:
        setModalDelete({
          show: true,
          items: [Id],
          allMessageArchived: Archived
        })
        break
      case ACTION_DOWNLOAD_HTML:
        handleHtmlDownload(Id)
        break
      case ACTION_DOWNLOAD_PDF:
        handlePdfDownload(Id)
        break

      default:
        break
    }
  }

  const handleCounterClick = e => {
    e.preventDefault()
    notifyParent('redirectMainFrame', [
      `statistics_app_redirector.aspx?r=/stats/sendouts/sending/mode/messages/0/${Id}`
    ])
  }

  const handleClickPublished = () => {
    notifyParent('openWebLibrary', [])
  }

  const handleClickThumbnail = () => {
    notifyParent('openPreviewMainFrame', [Id])
  }

  /* eslint-disable prettier/prettier */
  const actionItems = [
    ...(canEditBee
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionMessageOverview'
            }),
            onClick: () => handleClickAction(ACTION_OVERVIEW),
            className: 'test-changeOptionBee',
            'data-mu-test': 'action__overview'
          },
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionEdit'
            }),
            onClick: () => handleClickAction(ACTION_EDIT),
            className: 'test-editBee',
            'data-mu-test': 'action__edit'
          }
        ]
      : []),

    ...(canEditHtml
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionMessageOverview'
            }),
            onClick: () => handleClickAction(ACTION_OVERVIEW),
            className: 'test-messageOverview',
            'data-mu-test': 'action__overview'
          },
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionEdit'
            }),
            onClick: () => handleClickAction(ACTION_EDIT),
            className: 'test-edit',
            'data-mu-test': 'action__edit'
          }
        ]
      : []),
    ...(canSend
      ? [
          {
            separator: true,
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionSend'
            }),
            onClick: () => handleClickAction(ACTION_SEND),
            className: 'test-send',
            'data-mu-test': 'action__send'
          }
        ]
      : []),
    ...(canQuickSend
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionQuickSend'
            }),
            onClick: () => handleClickAction(ACTION_QUICKSEND),
            className: 'test-quickSend',
            'data-mu-test': 'action__quick-send'
          }
        ]
      : []),

    ...(canUnpublish && Archived
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionFooterNotArchive'
            }),
            onClick: () => handleClickAction(ACTION_UNARCHIVE),
            className: 'test-unArchive',
            'data-mu-test': 'action__unarchive'
          }
        ]
      : []),
    ...(canUnpublish && Published
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionFooterUnpublish'
            }),
            onClick: () => handleClickAction(ACTION_UNPUBLISH),
            className: 'test-unPublish',
            'data-mu-test': 'action__unpublish'
          }
        ]
      : []),

    ...[
      {
        separator: true,
        label: intl.formatMessage({
          id: 'Load.EmailMessageOverview.DownloadHtml'
        }),
        onClick: () => handleClickAction(ACTION_DOWNLOAD_HTML),
        className: 'test-copy',
        'data-mu-test': 'action__DownloadHtml'
      }
    ],

    ...(IsBee
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageOverview.DownloadPdf'
            }),
            onClick: () => handleClickAction(ACTION_DOWNLOAD_PDF),
            className: 'test-copy',
            'data-mu-test': 'action__DownloadPdf'
          }
        ]
      : []),

    ...(canCopy
      ? [
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionCopy'
            }),
            onClick: () => handleClickAction(ACTION_COPY),
            className: 'test-copy',
            'data-mu-test': 'action__copy'
          }
        ]
      : []),
    ...(hasStats && !!LastSent && !Archived
      ? [
          {
            separator: true,
            label: intl.formatMessage({
              id: 'Load.MailupCommon.ActionStatistics'
            }),
            onClick: () => handleClickAction(ACTION_STATISTICS),
            className: 'test-statistic',
            'data-mu-test': 'action__statistics'
          },
          {
            label: intl.formatMessage({
              id: 'Load.EmailMessageList.ActionLastSendingStatistics'
            }),
            onClick: () => handleClickAction(ACTION_LAST_SENDING_STATS),
            className: 'test-last-sending-statistic',
            'data-mu-test': 'action__last-sending-statistics'
          }
        ]
      : []),

    ...(evalCondition('NOT uo:NONL')
      ? [
          {
            separator: true,
            label: intl.formatMessage({ id: 'Load.MailupCommon.Delete' }),
            onClick: () => handleClickAction(ACTION_DELETE),
            'data-mu-test': 'action__delete',
            className: 'test-action__delete'
          }
        ]
      : [])
  ]
  /* eslint-enable prettier/prettier */

  const EXTRA_COLUMN_BEFORE_WIDTH = '15px'
  const EXTRA_COLUMN_AFTER_WIDTH = '26px'

  return (
    <>
      <DataListRow key={Id} data-mu-test='result'>
        <DataListExtraColumn before width={EXTRA_COLUMN_BEFORE_WIDTH}>
          <ResultCheckbox loading={loading} id={Id} />
        </DataListExtraColumn>
        <Grid cols={12}>
          <GridCell span={7}>
            {loading && (
              <>
                <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                <br />
                <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
                <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
              </>
            )}

            {!loading && (
              <Flex>
                <FlexItem flex='0 0 92px' margin='0 16px 0 4px'>
                  <ResultThumbnail
                    onClick={handleClickThumbnail}
                    messageId={Id}
                  />
                </FlexItem>
                <FlexItem style={{ overflow: 'hidden' }}>
                  <div
                    className='c-message-list-item__info'
                    data-mu-test='result__info'
                  >
                    <Link
                      variant={LINK_VARIANT_HIGHLIGHT}
                      onClick={() => handleClickAction(ACTION_OVERVIEW)}
                    >
                      <TruncateAndTooltip
                        text={Subject}
                        className='c-message-list-item__subject test-goToOverview'
                      />
                    </Link>
                    <Text
                      htmlElement={'span'}
                      variant={TEXT_VARIANT_SMALL_LIGHT}
                    >
                      <TruncateAndTooltip
                        className='c-message-list-item__note'
                        text={`${Id} ${Note && ` - ${Note}`}`}
                      />
                    </Text>
                  </div>
                  <SpacingVertical variant={SPACING_16} />
                  <div data-mu-test='result__dates'>
                    <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
                      {intl.formatMessage({
                        id: 'Load.EmailMessageList.LastUpdateHeaderColumn'
                      }) + ': '}
                      <Text
                        htmlElement={'span'}
                        variant={TEXT_VARIANT_SMALL_LIGHT}
                      >
                        {fromUTCtoZoneFormatted(
                          LastUpdated,
                          tzcode,
                          'YYYY-MM-DD'
                        )}
                      </Text>
                    </Text>

                    {LastSent && (
                      <>
                        <Text variant={TEXT_VARIANT_BOLD_LIGHT}>
                          {intl.formatMessage({
                            id: 'Load.EmailMessageList.LastSentHeaderColumn'
                          }) + ': '}
                          <Text
                            htmlElement={'span'}
                            variant={TEXT_VARIANT_SMALL_LIGHT}
                          >
                            {fromUTCtoZoneFormatted(
                              LastSent,
                              tzcode,
                              'YYYY-MM-DD'
                            )}
                          </Text>
                        </Text>
                      </>
                    )}
                  </div>
                  <div data-mu-test='result__tags'>
                    <SpacingVertical variant={SPACING_16} />
                    <Text
                      htmlElement={'span'}
                      variant={TEXT_VARIANT_BOLD_LIGHT}
                    >
                      {intl.formatMessage({ id: 'Load.Common.Tag' })}:{' '}
                    </Text>
                    {tags.length ? (
                      <Flex
                        display='inline-flex'
                        flexWrap='wrap'
                        alignItems='baseline'
                      >
                        {tags.map(
                          (tag, i) =>
                            i < MAX_TAGS_PER_ROW && (
                              <FlexItem
                                margin='4px 8px 0 0'
                                key={`${Id}-tag-${i}-${tag.value}`}
                              >
                                <Badge text={tag.label} lowercase />
                              </FlexItem>
                            )
                        )}

                        {tags.length > MAX_TAGS_PER_ROW && (
                          <Text
                            htmlElement={'span'}
                            variant={TEXT_VARIANT_BOLD_LIGHT}
                            className='c-message-list-item__tags__more'
                          >
                            {intl.formatMessage(
                              { id: 'Load.Common.MoreElements' },
                              { X: `+${tags.length - MAX_TAGS_PER_ROW}` }
                            )}
                          </Text>
                        )}
                      </Flex>
                    ) : (
                      <Text htmlElement={'span'} variant={TEXT_VARIANT_LIGHT}>
                        -
                      </Text>
                    )}
                  </div>
                  {[MESSAGE_TYPE_CONFIRMATION, MESSAGE_TYPE_WELCOME].includes(
                    MessageType
                  ) && (
                    <>
                      <SpacingVertical variant={SPACING_16} />
                      <TypeComponent
                        isActiveMessage={isActiveMessage}
                        isUsed={isUsed}
                        Id={Id}
                        Subject={Subject}
                        mode={MessageType}
                      />
                    </>
                  )}
                </FlexItem>
              </Flex>
            )}
          </GridCell>
          <GridCell span={1}>
            {loading && (
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            )}
            {!loading && (
              <div
                className={classnames('c-message-list-item__status', {
                  'c-message-list-item__status--archived': Archived,
                  'c-message-list-item__status--published': Published
                })}
              >
                {Archived && (
                  <Icon
                    family='sli'
                    icon='archived'
                    tooltipText={intl.formatMessage({
                      id: 'Load.EmailMessageList.ArchivedSearchOptions'
                    })}
                    className='c-message-list-item__status__archived'
                  />
                )}
                {Published && !EnableWebLibrary && (
                  <Icon
                    family='sli'
                    icon='published'
                    tooltipText={intl.formatMessage({
                      id: 'Load.EmailMessageList.PublishedSearchOptions'
                    })}
                    className='c-message-list-item__status__published'
                  />
                )}
                {Published && EnableWebLibrary && (
                  <Link
                    href='#'
                    variant={LINK_VARIANT_HIGHLIGHT}
                    className='test-goToPublished'
                    onClick={handleClickPublished}
                  >
                    <Icon
                      family='sli'
                      icon='published'
                      tooltipText={intl.formatMessage({
                        id: 'Load.EmailMessageList.PublishedTooltip'
                      })}
                      className='c-message-list-item__status__published'
                    />
                  </Link>
                )}
              </div>
            )}
          </GridCell>
          <GridCell span={2}>
            {loading && (
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            )}
            {!loading && LastSent && sentCount.state === 'loading' && (
              <SkeletonLoader variant={SKELETON_LOADER_VARIANT_ONWHITE} />
            )}
            {!loading && (
              <div
                className='c-message-list-item__counts'
                data-mu-test='result__counts'
              >
                {LastSent ? (
                  <>
                    {/* {sentCount.state === 'loading' && (
                      <span className='is-loading' />
                    )} */}
                    {sentCount.state === 'hasValue' && (
                      <>
                        {evalCondition('uo:STAT') ? (
                          <Link
                            variant={LINK_VARIANT_HIGHLIGHT}
                            href='#'
                            onClick={handleCounterClick}
                            className='c-message-list-item__count c-message-list-item__count--sent test-goToStatistic'
                          >
                            {sentCount.contents}
                          </Link>
                        ) : (
                          <Text
                            htmlElement={'span'}
                            variant={TEXT_VARIANT_BOLD_LIGHT}
                            className='c-message-list-item__count c-message-list-item__count--sent'
                          >
                            {sentCount.contents}ss
                          </Text>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <Text
                    htmlElement={'span'}
                    variant={TEXT_VARIANT_BOLD_LIGHT}
                    className='c-message-list-item__count c-message-list-item__count--sent'
                  >
                    {sentCount}
                  </Text>
                )}
                <Text
                  htmlElement={'p'}
                  variant={TEXT_VARIANT_LIGHT}
                  className='c-message-list-item__sent-note'
                >
                  {intl.formatMessage({ id: 'Load.MailupCommon.Sent' })}
                </Text>
              </div>
            )}
          </GridCell>
          <GridCell span={2}>
            {!hideCollaboration && (
              <ResultCollaboration
                collaborationEnabled={collaborationEnabled}
                buyCollaboration={buyCollaboration}
                Id={Id}
                Subject={Subject}
                loading={loading}
              />
            )}
          </GridCell>
        </Grid>
        <DataListExtraColumn after width={EXTRA_COLUMN_AFTER_WIDTH}>
          <Actions
            disabled={loading}
            id={Id}
            className='test-actions'
            items={actionItems}
          />
        </DataListExtraColumn>
      </DataListRow>
    </>
  )
}

Result.propTypes = {
  message: PropTypes.shape({
    Archived: PropTypes.bool,
    Id: PropTypes.number,
    IsBee: PropTypes.bool,
    LastSent: PropTypes.instanceOf(Date),
    LastUpdated: PropTypes.instanceOf(Date),
    MessageType: PropTypes.string,
    Note: PropTypes.string,
    Published: PropTypes.bool,
    SentCount: PropTypes.number,
    Subject: PropTypes.string,
    collaboration: PropTypes.object,
    isActiveMessage: PropTypes.bool,
    tags: PropTypes.array
  }),
  hideCollaboration: PropTypes.bool,
  collaborationEnabled: PropTypes.bool,
  buyCollaboration: PropTypes.bool
}
